import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Modal, IconButton, Paper, List, ListItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { images } from 'assets/images';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import { useCustomPortalSections, useAuth, useAlert } from 'hooks';
import { hideModal } from 'state/actions';
import { clone } from 'lodash';
import CloseIcon from '@material-ui/icons/Close';
import {
  COLOR,
  PORTAL_CUSTOM_SECTIONS_TEMPLATES,
  MODAL,
  PORTAL_CUSTOM_SECTIONS,
} from 'config/constants';
import { setDataChanged, setSection, setSections } from 'state/slices/publicPersonalPortal';

const useStyles = makeStyles({
  content: {
    padding: 50,
  },
  templatesContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  modal: {
    position: 'absolute',
    top: '50%',
    padding: 50,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    left: '50%',
    backgroundColor: COLOR.WHITE,
    borderRadius: 10,
    transform: 'translate(-50%, -50%)',
    height: 'auto',
    width: 'auto',
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: COLOR.LIGHT_BLUE,
    marginBottom: 10,
  },
  selectedTemplateContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
    '&:hover': {
      cursor: 'pointer',
      transition: 'transform 0.3s cubic-bezier(0.455, 0.03, 0.525, 0.955)',
    },
    borderRadius: 8,
    border: `2px solid ${COLOR.LIGHT_BLUE}`,
  },
  image: {
    borderRadius: 8,
    width: 450,
    '@media (max-width: 768px)': {
      width: 250,
    },
  },
  templateContent: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
    borderRadius: 8,
    '&:hover': {
      cursor: 'pointer',
      transform: 'translateY(-5px)',
      transition: 'transform 0.3s cubic-bezier(0.455, 0.03, 0.525, 0.955)',
    },
  },
  icon: {
    position: 'absolute',
    top: 10,
    right: 20,
    color: COLOR.LIGHT_BLUE,
  },
});

function PortalSectionTemplateModal() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { sections, section, broker_portal_infos } = useCustomPortalSections();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const fmtTemplate = intl.formatMessage({ id: 'portal.template' });

  const getTemplates = () => {
    switch (section.key) {
      case PORTAL_CUSTOM_SECTIONS.TESTIMONIAL.key:
        return [
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.BLOCK, src: images.examples.testimonial_block },
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.SIMPLE, src: images.examples.testimonial_simple },
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.LIST, src: images.examples.testimonial_list },
        ];
      case PORTAL_CUSTOM_SECTIONS.ABOUT.key:
        return [
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.SIMPLE, src: images.examples.about_simple },
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.LIST, src: images.examples.about_list },
          { key: PORTAL_CUSTOM_SECTIONS_TEMPLATES.BLOCK, src: images.examples.about_block },
        ];
      default:
        return [];
    }
  };

  useEffect(() => {
    setSelectedTemplate(section.template);
    setTemplates(getTemplates());
  }, [section]);

  const onClose = () => {
    dispatch(hideModal(MODAL.SECTION_TEMPLATE));
  };
  const onSelectTemplate = (template) => {
    setSelectedTemplate(template);
    const newSection = {
      ...section,
      template,
    };
    dispatch(setSection(newSection));
    const foundExistingSection = sections.find((s) => {
      return s.section.key === newSection.key;
    });
    let newSections = clone(sections);
    if (!foundExistingSection) {
      newSections.push({ section: newSection });
    } else {
      newSections = sections.map((s) => {
        if (s.section.key === newSection.key) {
          return { ...s, section: newSection };
        }
        return s;
      });
    }
    dispatch(setSections(newSections));
    onClose();
    const foundSavedSection = broker_portal_infos.sections.find((s) => {
      return s.section.key === newSection.key;
    });
    if (foundSavedSection && foundSavedSection?.section?.template === template) {
      dispatch(setDataChanged(false));
    }
  };

  const renderItem = (template, index) => {
    return (
      <ListItem key={template.key}>
        <Paper
          className={
            selectedTemplate === template.key
              ? classes.selectedTemplateContent
              : classes.templateContent
          }
          onClick={() => {
            return onSelectTemplate(template.key);
          }}
        >
          {selectedTemplate === template.key ? (
            <CheckCircleRoundedIcon className={classes.icon} />
          ) : null}
          <img className={classes.image} src={template.src} alt='template' />
          <Typography>{`${fmtTemplate} ${index + 1}`}</Typography>
        </Paper>
      </ListItem>
    );
  };

  const content = () => {
    return (
      <Modal open>
        <div className={classes.modal}>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <div className={classes.templatesContent}>
            {templates.map((template, index) => {
              return renderItem(template, index);
            })}
          </div>
        </div>
      </Modal>
    );
  };
  return <div className={classes.content}>{content()}</div>;
}
export default PortalSectionTemplateModal;
